/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

import "./src/styles/global.css"

const getButtonLabel = el =>
  (el.getAttribute("aria-label") || el.textContent || el.value || "").trim().slice(0, 100)

// The current page/domain name, e.g. "Domain 1: Acceptability & Culture" or
// "About & Disclaimer" — read from the breadcrumb's active item so every
// Segment call (page views, button clicks, field interactions) can be tagged
// with it. Falls back to the document title (site name suffix stripped) for
// pages without a breadcrumb, e.g. the homepage.
const getDomainTitle = () => {
  const activeBreadcrumb = document.querySelector(".breadcrumb-item.active")
  const breadcrumbText = activeBreadcrumb && activeBreadcrumb.textContent.trim()
  if (breadcrumbText) return breadcrumbText

  return document.title.split("|")[0].trim()
}

// Forces any link pointing to a different site to open in a new tab. Covers
// hand-written <a> tags but especially ones we don't directly control — links
// embedded in CMS content (markdown descriptions, CKEditor question/guidance
// text, disclaimers) rendered via dangerouslySetInnerHTML, which otherwise
// ship with no target/rel at all.
const externalizeAnchor = a => {
  if (a.target) return // already explicitly set (e.g. CMS openInNewTab toggle)
  let url
  try {
    url = new URL(a.getAttribute("href"), window.location.origin)
  } catch (e) {
    return
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return
  if (url.hostname === window.location.hostname) return

  a.setAttribute("target", "_blank")
  const rel = new Set((a.getAttribute("rel") || "").split(/\s+/).filter(Boolean))
  rel.add("noopener")
  rel.add("noreferrer")
  a.setAttribute("rel", Array.from(rel).join(" "))
}

const externalizeLinksIn = root => {
  root.querySelectorAll("a[href]").forEach(externalizeAnchor)
}

// Tracks play/pause/complete/progress on embedded YouTube videos (CMS content
// authors paste a raw <iframe> via the section-header description's "html"
// code block — see SectionHeader.js) using the YouTube IFrame Player API's
// postMessage bridge, so no video ever needs re-hosting just to get events.
const YOUTUBE_HOSTNAMES = ["youtube.com", "youtube-nocookie.com", "youtu.be"]

const isYouTubeIframe = iframe => {
  try {
    const { hostname } = new URL(iframe.getAttribute("src"), window.location.origin)
    return YOUTUBE_HOSTNAMES.some(host => hostname === host || hostname.endsWith(`.${host}`))
  } catch (e) {
    return false
  }
}

// The postMessage bridge only works once the iframe's own src carries these
// params, so on first sight of a CMS-pasted embed (which won't have them) we
// rewrite src in place — a one-time reload of that particular video, not the
// page — before ever calling YT.Player on it.
const ensureYouTubeJsApi = iframe => {
  let url
  try {
    url = new URL(iframe.getAttribute("src"), window.location.origin)
  } catch (e) {
    return
  }
  let changed = false
  if (url.searchParams.get("enablejsapi") !== "1") {
    url.searchParams.set("enablejsapi", "1")
    changed = true
  }
  if (!url.searchParams.get("origin")) {
    url.searchParams.set("origin", window.location.origin)
    changed = true
  }
  if (changed) iframe.src = url.toString()
}

let youTubeApiPromise = null
const loadYouTubeApi = () => {
  if (youTubeApiPromise) return youTubeApiPromise
  youTubeApiPromise = new Promise(resolve => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT)
      return
    }
    const previousCallback = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      if (previousCallback) previousCallback()
      resolve(window.YT)
    }
    const script = document.createElement("script")
    script.src = "https://www.youtube.com/iframe_api"
    document.head.appendChild(script)
  })
  return youTubeApiPromise
}

const trackVideoEvent = (name, player, extra) => {
  if (!window.analytics) return
  const videoData = (player.getVideoData && player.getVideoData()) || {}
  window.analytics.track(name, {
    videoTitle: videoData.title || undefined,
    videoId: videoData.video_id || undefined,
    path: window.location.pathname,
    domainTitle: getDomainTitle(),
    ...extra,
  })
}

const PROGRESS_MILESTONES = [25, 50, 75]

// Polls currentTime/duration while playing to fire 25/50/75% milestones —
// the IFrame API has no native progress event. Stops itself on pause, on
// reaching 75%, or if the player disappears (page navigated away).
const pollVideoProgress = (player, seenMilestones) => {
  const interval = setInterval(() => {
    if (!player.getDuration || !player.getCurrentTime) {
      clearInterval(interval)
      return
    }
    const duration = player.getDuration()
    if (!duration) return
    const percentComplete = (player.getCurrentTime() / duration) * 100
    PROGRESS_MILESTONES.forEach(milestone => {
      if (percentComplete >= milestone && !seenMilestones.has(milestone)) {
        seenMilestones.add(milestone)
        trackVideoEvent("Video Progress", player, { percent: milestone })
      }
    })
    if (player.getPlayerState() !== 1 || seenMilestones.size === PROGRESS_MILESTONES.length) {
      clearInterval(interval)
    }
  }, 1000)
}

const trackedYouTubeIframes = new WeakSet()

const attachYouTubeTracking = iframe => {
  if (trackedYouTubeIframes.has(iframe)) return
  trackedYouTubeIframes.add(iframe)
  ensureYouTubeJsApi(iframe)

  loadYouTubeApi().then(YT => {
    const seenMilestones = new Set()
    let hasStartedPlaying = false
    new YT.Player(iframe, {
      events: {
        onStateChange: event => {
          const player = event.target
          if (event.data === YT.PlayerState.PLAYING) {
            trackVideoEvent(hasStartedPlaying ? "Video Resumed" : "Video Played", player)
            hasStartedPlaying = true
            pollVideoProgress(player, seenMilestones)
          } else if (event.data === YT.PlayerState.PAUSED) {
            trackVideoEvent("Video Paused", player)
          } else if (event.data === YT.PlayerState.ENDED) {
            trackVideoEvent("Video Completed", player)
          }
        },
      },
    })
  })
}

const attachYouTubeTrackingIn = root => {
  root.querySelectorAll("iframe").forEach(iframe => {
    if (isYouTubeIframe(iframe)) attachYouTubeTracking(iframe)
  })
}

// Browser exit-confirmation reminding users to complete the feedback survey.
// Note: Chrome, Firefox, Safari and Edge all ignore custom beforeunload text
// and show their own generic message ("Leave site? Changes you made may not
// be saved") — this is a deliberate browser security restriction with no
// code-level workaround. We still set a custom message for the rare browser
// that honours it (e.g. legacy IE); on everything else, the confirmation
// itself still appears, just with the browser's own wording.
//
// beforeunload fires on tab close, refresh, and full-page navigations — but
// not on Gatsby client-side route changes. When the user clicks a link that
// opens another page (in-site or external, same tab), we temporarily suppress
// the prompt so it only appears when closing the browser/tab (or refreshing).
const FEEDBACK_SURVEY_EXIT_MESSAGE =
  "Finished with the tool? Please complete our Feedback Survey before you go — your feedback helps us improve it."

let allowUnload = false
let allowUnloadTimer = null

const allowNextUnload = () => {
  allowUnload = true
  if (allowUnloadTimer) clearTimeout(allowUnloadTimer)
  allowUnloadTimer = setTimeout(() => {
    allowUnload = false
    allowUnloadTimer = null
  }, 1000)
}

const confirmExitForFeedback = event => {
  if (allowUnload) return
  if (window.location.pathname === "/feedback/") return
  event.preventDefault()
  event.returnValue = FEEDBACK_SURVEY_EXIT_MESSAGE
  return FEEDBACK_SURVEY_EXIT_MESSAGE
}

const linkOpensInNewTab = (link, event) =>
  link.target === "_blank" ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.button === 1

const isSamePageHashLink = url =>
  url.origin === window.location.origin &&
  url.pathname === window.location.pathname &&
  Boolean(url.hash)

const isNavigatingAwayViaLink = (link, event) => {
  const href = link.getAttribute("href")
  if (!href || href.startsWith("#")) return false

  let url
  try {
    url = new URL(href, window.location.origin)
  } catch (e) {
    return false
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return false
  if (isSamePageHashLink(url)) return false
  if (linkOpensInNewTab(link, event)) return false

  return true
}

const scrollToPageTop = () => {
  if (typeof window === "undefined") return
  window.scrollTo({ top: 0, left: 0, behavior: "instant" })
}

const scrollToHashTarget = hash => {
  if (!hash || typeof window === "undefined") return false
  const id = decodeURIComponent(hash.replace(/^#/, ""))
  const node = document.getElementById(id)
  if (!node) return false
  node.scrollIntoView({ behavior: "instant", block: "start" })
  return true
}

// Cross-page hash links (e.g. Resources → domain question) fire before Gatsby
// finishes rendering the destination page, so the anchor often isn't in the DOM yet.
const scrollToHashTargetAfterRender = hash => {
  if (!hash || typeof window === "undefined") return

  if (scrollToHashTarget(hash)) return

  let attempts = 0
  const maxAttempts = 25
  let timer = null
  const retry = () => {
    attempts += 1
    if (scrollToHashTarget(hash) || attempts >= maxAttempts) {
      if (timer) clearInterval(timer)
    }
  }

  requestAnimationFrame(retry)
  requestAnimationFrame(() => requestAnimationFrame(retry))
  window.setTimeout(retry, 0)
  window.setTimeout(retry, 50)
  timer = window.setInterval(retry, 100)
}

const shouldScrollToTop = (location, prevLocation) => {
  if (!location || location.hash) return false
  // Preserve Gatsby's saved scroll position for browser back/forward.
  if (location.action === "POP") return false
  if (!prevLocation) return false
  return prevLocation.pathname !== location.pathname
}

// First client-side navigation can finish before the new page paints; repeat
// scroll after layout so we beat scroll anchoring and saved-position restore.
const scrollToPageTopAfterRender = () => {
  scrollToPageTop()
  requestAnimationFrame(() => {
    scrollToPageTop()
    requestAnimationFrame(scrollToPageTop)
  })
  window.setTimeout(scrollToPageTop, 0)
  window.setTimeout(scrollToPageTop, 50)
}

export const onClientEntry = () => {
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual"
  }

  window.addEventListener("beforeunload", confirmExitForFeedback)

  // Suppress the leave confirmation when the user clicks through to another
  // page in the same tab (nav links, breadcrumbs, Continue buttons, etc.).
  document.addEventListener(
    "click",
    event => {
      const link = event.target.closest("a[href]")
      if (link && isNavigatingAwayViaLink(link, event)) {
        allowNextUnload()
      }
    },
    true
  )

  externalizeLinksIn(document.body)
  attachYouTubeTrackingIn(document.body)

  // Catches links/videos added later without a route change: CKEditor/markdown
  // content mounted behind a toggle (guidance panels), modals, remounted
  // components (Clear Form), etc. Only fires on element add/remove, not on
  // every keystroke or attribute change, so it stays cheap.
  if ("MutationObserver" in window) {
    new MutationObserver(() => {
      externalizeLinksIn(document.body)
      attachYouTubeTrackingIn(document.body)
    }).observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  document.addEventListener("click", event => {
    const button = event.target.closest(
      "button, [role='button'], input[type='submit'], input[type='button']"
    )
    if (button && window.analytics) {
      window.analytics.track("Button Clicked", {
        label: getButtonLabel(button),
        id: button.id || undefined,
        path: window.location.pathname,
        domainTitle: getDomainTitle(),
      })
    }

    // Separate from "Button Clicked": any <a> pointing off-site (the same
    // check externalizeAnchor uses to decide whether to force a new tab),
    // covering both hand-written links and CMS/CKEditor content.
    const link = event.target.closest("a[href]")
    if (!link || !window.analytics) return
    let url
    try {
      url = new URL(link.getAttribute("href"), window.location.origin)
    } catch (e) {
      return
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return
    if (url.hostname === window.location.hostname) return

    window.analytics.track("External Link Clicked", {
      label: getButtonLabel(link),
      url: url.href,
      path: window.location.pathname,
      domainTitle: getDomainTitle(),
    })
  })

  // Tracks that a user interacted with a form field (typed, selected, checked)
  // without ever reading its value/checked state — only fields explicitly
  // tagged with data-field-id are reported, and only that safe identifier
  // plus the field type/question are sent, never the entered/selected content.
  document.addEventListener("change", event => {
    const fieldId = event.target.dataset && event.target.dataset.fieldId
    if (!fieldId || !window.analytics) return

    window.analytics.track("Field Interacted", {
      fieldId,
      fieldType: event.target.dataset.fieldType || event.target.tagName.toLowerCase(),
      question: event.target.dataset.fieldQuestion || undefined,
      path: window.location.pathname,
      domainTitle: getDomainTitle(),
    })
  })

  // Fires the initial pageview. The Segment snippet (see gatsby-ssr.js) only
  // loads analytics.js — it doesn't call page() itself, since that runs in
  // <head> before <body> exists and getDomainTitle() would have nothing to read.
  if (window.analytics) {
    window.analytics.page({ domainTitle: getDomainTitle() })
  }

  if (window.location.hash) {
    scrollToHashTargetAfterRender(window.location.hash)
  }
}

// Client-side route changes (nav menu, Continue buttons via navigate(), etc.)
// don't normally trigger beforeunload, but set the flag anyway for any edge
// case where a transition coincides with a full document unload.
export const onPreRouteUpdate = ({ location, prevLocation }) => {
  allowNextUnload()
  if (shouldScrollToTop(location, prevLocation)) {
    scrollToPageTop()
  }
}

// Scroll to top when moving between pages (e.g. "Go to next Domain"), but
// preserve default behaviour for in-page hash links and browser back/forward.
// Return false after manually scrolling — gatsby-react-router-scroll treats a
// truthy return as permission to restore the saved Y position, not as coords.
export const shouldUpdateScroll = ({
  routerProps: { location },
  prevRouterProps,
}) => {
  if (location.hash) return false
  if (location.action === "POP") return true
  if (
    prevRouterProps &&
    prevRouterProps.location.pathname === location.pathname
  ) {
    return false
  }
  scrollToPageTopAfterRender()
  return false
}

// Covers subsequent client-side route changes (the initial load is handled by
// onClientEntry above).
//
// Gatsby's Head API patches document.title asynchronously after onRouteUpdate
// fires, and the delay isn't fixed (varies with load), so a flat setTimeout
// isn't reliable. Instead, watch the <title> element for its actual mutation
// and fire analytics.page() right when it lands, with a fallback timeout in
// case two consecutive pages happen to share the same title (no mutation).
export const onRouteUpdate = ({ location, prevLocation }) => {
  allowUnload = false
  if (allowUnloadTimer) {
    clearTimeout(allowUnloadTimer)
    allowUnloadTimer = null
  }

  if (shouldScrollToTop(location, prevLocation)) {
    scrollToPageTopAfterRender()
  } else if (location.hash) {
    scrollToHashTargetAfterRender(location.hash)
  }

  if (!window.analytics) return

  const titleAtRouteUpdate = document.title
  let fired = false
  let observer = null

  const firePageCall = () => {
    if (fired) return
    fired = true
    if (observer) observer.disconnect()
    window.analytics.page({ domainTitle: getDomainTitle() })
  }

  // Watch the whole <head> (not just the <title> element) since Gatsby may
  // replace the title node rather than mutate it in place. Only fire early
  // once document.title has genuinely changed, so unrelated head mutations
  // (e.g. meta tags) don't trigger a premature call.
  if ("MutationObserver" in window) {
    observer = new MutationObserver(() => {
      if (document.title !== titleAtRouteUpdate) {
        firePageCall()
      }
    })
    observer.observe(document.head, { childList: true, subtree: true, characterData: true })
  }

  // Fallback in case the title doesn't change (two consecutive pages sharing
  // a title) or MutationObserver is unavailable.
  setTimeout(firePageCall, 300)
}
